import type { BoxProps } from '../box'
import type { PaginationButtonProps, PaginationPrevNextProps } from './pagination.types'

import Box from '../box'
import Button from '../button'
import { useStyleConfig, vui } from '../core'
import { cs } from '../utils'
import { usePaginationContext } from './context'

/** Displays a page navigation button within the Pagination. */
export const PaginationButton = vui<'button', PaginationButtonProps>((props, ref) => {
  const { className, page: pageProp, ...rest } = props
  const { onPageChange, page: currentPage } = usePaginationContext()
  const styles = useStyleConfig('Pagination', usePaginationContext())

  const isActive = pageProp === currentPage

  return (
    <Button
      className={cs('vui-paginationButton', className)}
      onClick={() => onPageChange(pageProp)}
      ref={ref}
      text={pageProp}
      isActive={isActive}
      {...styles.button}
      {...rest}
    />
  )
})

PaginationButton.displayName = 'PaginationButton'

// ---------------------------------------------------------------------------

/** Displays '...' ellipsis for hidden pages. */
export const PaginationEllipsis = vui<'span', BoxProps>((props, ref) => {
  const { children, className, ...rest } = props
  const styles = useStyleConfig('Pagination', usePaginationContext())

  return (
    <Box
      alignItems="center"
      className={cs('vui-paginationEllipsis', className)}
      px="10px"
      ref={ref}
      {...styles.ellipsis}
      {...rest}
    >
      {children ?? '…'}
    </Box>
  )
})

PaginationEllipsis.displayName = 'PaginationEllipsis'

// ---------------------------------------------------------------------------

/** Displays a page navigation button, which navigates forward or backward. */
export const PaginationPrevNext = vui<'button', PaginationPrevNextProps>((props, ref) => {
  const { className, direction, ...rest } = props
  const { onPageChange, page, pageCount } = usePaginationContext()
  const styles = useStyleConfig('Pagination', usePaginationContext())

  const isPrev = direction === 'previous'
  const pageChange = isPrev ? -1 : 1
  const directionProps = {
    disabled: isPrev ? page === 1 : page === pageCount,
    icon: `uiAngle${isPrev ? 'Left' : 'Right'}`,
    onClick: () => onPageChange(page + pageChange),
  }
  return (
    <Button
      className={cs('vui-paginationPrevNext', className)}
      minW={0}
      ref={ref}
      {...styles.prevNext}
      {...directionProps}
      {...rest}
    />
  )
})

PaginationPrevNext.displayName = 'PaginationPrevNext'
